import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  alpha,
  Box,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import RedoIcon from '@mui/icons-material/Redo';
import SearchIcon from '@mui/icons-material/Search';
import TitleIcon from '@mui/icons-material/Title';
import UndoIcon from '@mui/icons-material/Undo';
import { registerDragonSupport } from '@lexical/dragon';
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  AutoLinkNode,
  createLinkMatcherWithRegExp,
  LinkNode,
} from '@lexical/link';
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListItemNode,
  ListNode,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import {
  createDOMRange,
  $forEachSelectedTextNode,
  $setBlocksType,
} from '@lexical/selection';
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  $isQuoteNode,
  HeadingNode,
  QuoteNode,
  registerRichText,
} from '@lexical/rich-text';
import { $findTextIntersectionFromCharacters } from '@lexical/text';
import {
  $getNearestNodeOfType,
  mergeRegister,
  positionNodeOnRange,
} from '@lexical/utils';
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from 'lexical';

const AUTO_LINK_MATCHERS = [
  createLinkMatcherWithRegExp(
    /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/i,
    (text) => (text.startsWith('http') ? text : `https://${text}`)
  ),
  createLinkMatcherWithRegExp(
    /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i,
    (text) => `mailto:${text}`
  ),
];

const editorTheme = {
  paragraph: 'textlab-editor-paragraph',
  heading: {
    h1: 'textlab-editor-heading1',
    h2: 'textlab-editor-heading2',
  },
  list: {
    listitem: 'textlab-editor-list-item',
    nested: {
      listitem: 'textlab-editor-list-item-nested',
    },
    ol: 'textlab-editor-list-ordered',
    ul: 'textlab-editor-list-unordered',
  },
  link: 'textlab-editor-link',
  quote: 'textlab-editor-quote',
  text: {
    bold: 'textlab-editor-text-bold',
    italic: 'textlab-editor-text-italic',
    underline: 'textlab-editor-text-underline',
  },
};

function buildInitialEditorStateFromText(content) {
  return () => {
    const root = $getRoot();
    const lines = (content || '').replace(/\r\n?/g, '\n').split('\n');
    const normalizedLines = lines.length ? lines : [''];

    normalizedLines.forEach((line) => {
      const paragraph = $createParagraphNode();

      if (line) {
        paragraph.append($createTextNode(line));
      }

      root.append(paragraph);
    });
  };
}

function getSearchOffsets(text, query) {
  if (!query) {
    return [];
  }

  const haystack = text.toLowerCase();
  const needle = query.toLowerCase();
  const offsets = [];
  let fromIndex = 0;

  while (fromIndex <= haystack.length - needle.length) {
    const matchIndex = haystack.indexOf(needle, fromIndex);

    if (matchIndex === -1) {
      break;
    }

    offsets.push(matchIndex);
    fromIndex = matchIndex + needle.length;
  }

  return offsets;
}

function SearchHighlightPlugin({
  query,
  highlightColor,
  highlightOutline,
  onMatchCountChange,
}) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const normalizedQuery = query.trim().toLowerCase();
    let cleanups = [];

    const clearHighlights = () => {
      cleanups.forEach((cleanup) => cleanup());
      cleanups = [];
    };

    const decorateMatches = (editorState) => {
      clearHighlights();

      if (!normalizedQuery) {
        onMatchCountChange(0);
        return;
      }

      editorState.read(() => {
        const root = $getRoot();
        const searchableText = root
          .getAllTextNodes()
          .map((node) => node.getTextContent())
          .join('');
        const matchOffsets = getSearchOffsets(searchableText, normalizedQuery);

        onMatchCountChange(matchOffsets.length);

        matchOffsets.forEach((matchStart) => {
          const start = $findTextIntersectionFromCharacters(root, matchStart);
          const end = $findTextIntersectionFromCharacters(
            root,
            matchStart + normalizedQuery.length - 1
          );

          if (!start || !end) {
            return;
          }

          const range = createDOMRange(
            editor,
            start.node,
            start.offset,
            end.node,
            end.offset + 1
          );

          if (!range) {
            return;
          }

          cleanups.push(
            positionNodeOnRange(editor, range, (nodes) => {
              nodes.forEach((node) => {
                Object.assign(node.style, {
                  background: highlightColor,
                  border: `1px solid ${highlightOutline}`,
                  borderRadius: '4px',
                  pointerEvents: 'none',
                });
              });
            })
          );
        });
      });
    };

    decorateMatches(editor.getEditorState());

    const unregister = editor.registerUpdateListener(({ editorState }) => {
      decorateMatches(editorState);
    });

    return () => {
      unregister();
      clearHighlights();
    };
  }, [editor, highlightColor, highlightOutline, onMatchCountChange, query]);

  return null;
}

function RichTextSetupPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return mergeRegister(registerRichText(editor), registerDragonSupport(editor));
  }, [editor]);

  return null;
}

function RichTextToolbar({
  appearance,
  searchTerm,
  matchCount,
  onSearchChange,
  controlBorder,
  controlSurface,
}) {
  const [editor] = useLexicalComposerContext();
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [toolbarState, setToolbarState] = useState({
    blockType: 'paragraph',
    bold: false,
    italic: false,
    underline: false,
  });

  const refreshToolbarState = useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();

      if (!$isRangeSelection(selection)) {
        setToolbarState({
          blockType: 'paragraph',
          bold: false,
          italic: false,
          underline: false,
        });
        return;
      }

      const anchorNode = selection.anchor.getNode();
      const topLevelElement = anchorNode.getTopLevelElementOrThrow();
      const listNode = $getNearestNodeOfType(anchorNode, ListNode);
      let blockType = 'paragraph';

      if ($isListNode(listNode)) {
        blockType = listNode.getListType();
      } else if ($isHeadingNode(topLevelElement)) {
        blockType = topLevelElement.getTag();
      } else if ($isQuoteNode(topLevelElement)) {
        blockType = 'quote';
      }

      setToolbarState({
        blockType,
        bold: selection.hasFormat('bold'),
        italic: selection.hasFormat('italic'),
        underline: selection.hasFormat('underline'),
      });
    });
  }, [editor]);

  useEffect(() => {
    refreshToolbarState();

    return mergeRegister(
      editor.registerUpdateListener(() => {
        refreshToolbarState();
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          refreshToolbarState();
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor, refreshToolbarState]);

  const iconButtonSx = (active = false) => ({
    color: appearance.textColor,
    bgcolor: active ? alpha(appearance.textColor, 0.18) : controlSurface,
    border: '1px solid',
    borderColor: active ? alpha(appearance.textColor, 0.4) : controlBorder,
    '&:hover': {
      bgcolor: alpha(appearance.textColor, active ? 0.24 : 0.14),
    },
  });

  const searchFieldSx = {
    width: { xs: '100%', sm: 240 },
    '& .MuiOutlinedInput-root': {
      color: appearance.textColor,
      bgcolor: controlSurface,
      borderRadius: 1,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: controlBorder,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(appearance.textColor, 0.42),
    },
    '& .MuiInputBase-input::placeholder': {
      color: alpha(appearance.textColor, 0.62),
      opacity: 1,
    },
  };

  const setBlockType = (nextType) => {
    editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    editor.update(() => {
      const selection = $getSelection();

      if (!$isRangeSelection(selection)) {
        return;
      }

      if (nextType === 'paragraph') {
        $setBlocksType(selection, () => $createParagraphNode());
        return;
      }

      if (nextType === 'h1') {
        $setBlocksType(selection, () => $createHeadingNode('h1'));
      }

      if (nextType === 'quote') {
        $setBlocksType(selection, () => $createQuoteNode());
      }
    });
  };

  const toggleBlockType = (nextType) => {
    setBlockType(toolbarState.blockType === nextType ? 'paragraph' : nextType);
  };

  const toggleList = (type) => {
    const active = toolbarState.blockType === type;

    if (active) {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      return;
    }

    if (type === 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    }

    if (type === 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    }
  };

  const clearFormatting = () => {
    editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    editor.update(() => {
      const selection = $getSelection();

      if (!$isRangeSelection(selection)) {
        return;
      }

      $setBlocksType(selection, () => $createParagraphNode());
      $forEachSelectedTextNode((textNode) => {
        textNode.setFormat(0);
        textNode.setStyle('');
      });
    });
  };

  return (
    <Stack
      direction={{ xs: 'column', lg: 'row' }}
      spacing={1}
      sx={{
        flexShrink: 0,
        px: { xs: 1, sm: 2 },
        py: 1,
        borderBottom: 1,
        borderColor: alpha(appearance.textColor, 0.14),
        bgcolor: alpha(appearance.backgroundColor, 0.72),
        backdropFilter: 'blur(12px)',
      }}
    >
      <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
        {[
          {
            active: false,
            disabled: !canUndo,
            icon: <UndoIcon />,
            label: 'Undo',
            onClick: () => editor.dispatchCommand(UNDO_COMMAND, undefined),
          },
          {
            active: false,
            disabled: !canRedo,
            icon: <RedoIcon />,
            label: 'Redo',
            onClick: () => editor.dispatchCommand(REDO_COMMAND, undefined),
          },
          {
            active: toolbarState.bold,
            icon: <FormatBoldIcon />,
            label: 'Bold',
            onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold'),
          },
          {
            active: toolbarState.italic,
            icon: <FormatItalicIcon />,
            label: 'Italic',
            onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic'),
          },
          {
            active: toolbarState.underline,
            icon: <FormatUnderlinedIcon />,
            label: 'Underline',
            onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline'),
          },
          {
            active: toolbarState.blockType === 'h1',
            icon: <TitleIcon />,
            label: 'Heading',
            onClick: () => toggleBlockType('h1'),
          },
          {
            active: toolbarState.blockType === 'quote',
            icon: <FormatQuoteIcon />,
            label: 'Quote',
            onClick: () => toggleBlockType('quote'),
          },
          {
            active: toolbarState.blockType === 'bullet',
            icon: <FormatListBulletedIcon />,
            label: 'Bullet list',
            onClick: () => toggleList('bullet'),
          },
          {
            active: toolbarState.blockType === 'number',
            icon: <FormatListNumberedIcon />,
            label: 'Numbered list',
            onClick: () => toggleList('number'),
          },
          {
            active: false,
            icon: <FormatClearIcon />,
            label: 'Clear formatting',
            onClick: clearFormatting,
          },
        ].map((item) => (
          <Tooltip key={item.label} title={item.label}>
            <span>
              <IconButton
                aria-label={item.label}
                disabled={item.disabled}
                onMouseDown={(event) => event.preventDefault()}
                onClick={item.onClick}
                sx={iconButtonSx(item.active)}
              >
                {item.icon}
              </IconButton>
            </span>
          </Tooltip>
        ))}
      </Stack>

      <Box sx={{ flexGrow: 1 }} />

      <TextField
        size="small"
        value={searchTerm}
        placeholder="Find in file"
        onChange={(event) => onSearchChange(event.target.value)}
        sx={searchFieldSx}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <Typography
                  variant="caption"
                  sx={{ color: alpha(appearance.textColor, 0.72) }}
                  noWrap
                >
                  {matchCount}
                </Typography>
              </InputAdornment>
            ) : null,
          },
        }}
      />
    </Stack>
  );
}

export default function RichTextEditor({ file, appearance, onDocumentChange }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [matchCount, setMatchCount] = useState(0);
  const surfaceRef = useRef(null);

  useEffect(() => {
    setSearchTerm('');
    setMatchCount(0);
  }, [file.id]);

  useEffect(() => {
    const scrollToTop = () => {
      if (surfaceRef.current) {
        surfaceRef.current.scrollTop = 0;
        surfaceRef.current.scrollLeft = 0;
      }
    };

    let secondFrame = 0;
    scrollToTop();
    const firstFrame = requestAnimationFrame(() => {
      scrollToTop();
      secondFrame = requestAnimationFrame(scrollToTop);
    });

    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
    };
  }, [file.id]);

  const initialConfig = useMemo(
    () => ({
      namespace: `TextLab-${file.id}`,
      theme: editorTheme,
      nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, AutoLinkNode],
      onError(error) {
        throw error;
      },
      editorState: file.editorState || buildInitialEditorStateFromText(file.content),
    }),
    [file.content, file.editorState, file.id]
  );

  const controlBorder = alpha(appearance.textColor, 0.22);
  const controlSurface = alpha(appearance.textColor, 0.08);
  const highlightColor = alpha('#facc15', 0.42);
  const highlightOutline = alpha('#facc15', 0.88);

  return (
    <LexicalComposer key={file.id} initialConfig={initialConfig}>
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          '--textlab-editor-bg': appearance.backgroundColor,
          '--textlab-editor-text': appearance.textColor,
          '--textlab-editor-muted': alpha(appearance.textColor, 0.56),
          '--textlab-editor-link': '#2563eb',
          '--textlab-editor-font': appearance.fontFamily,
          '--textlab-editor-font-size': `${appearance.fontSize}px`,
          '--textlab-editor-line-height': appearance.lineHeight,
        }}
      >
        <RichTextToolbar
          appearance={appearance}
          searchTerm={searchTerm}
          matchCount={matchCount}
          onSearchChange={setSearchTerm}
          controlBorder={controlBorder}
          controlSurface={controlSurface}
        />

        <Box
          ref={surfaceRef}
          className="textlab-rich-editor__surface"
          sx={{
            position: 'relative',
            flexGrow: 1,
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          <ContentEditable
            className="textlab-rich-editor__input"
            aria-label="File editor"
            aria-placeholder="Start writing..."
            spellCheck
            placeholder={
              <Box className="textlab-rich-editor__placeholder">Start writing...</Box>
            }
          />
          <RichTextSetupPlugin />
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin validateUrl={() => true} />
          <AutoLinkPlugin matchers={AUTO_LINK_MATCHERS} />
          <ClickableLinkPlugin newTab />
          <AutoFocusPlugin defaultSelection="rootStart" />
          <OnChangePlugin
            ignoreSelectionChange
            onChange={(editorState) => {
              const plainText = editorState.read(() => $getRoot().getTextContent());
              const serializedState = JSON.stringify(editorState.toJSON());

              onDocumentChange({
                content: plainText,
                editorState: serializedState,
              });
            }}
          />
          <SearchHighlightPlugin
            query={searchTerm}
            highlightColor={highlightColor}
            highlightOutline={highlightOutline}
            onMatchCountChange={setMatchCount}
          />
        </Box>
      </Box>
    </LexicalComposer>
  );
}
